import React, { createContext, useState, ReactNode, useEffect, useCallback } from "react";
import { ActionItem, ChannelState, ChannelSubstate, ChannelType, SKChannel, Tier } from "../models/SKChannel";
import {
  createChannel,
  createChannelEdits,
  deleteChannel,
  getMyChannel,
  getChannelEdits,
  getUserCreatedChannel,
  updateChannel,
  updateChannelEdits,
  updateChannelStatus,
  updateChannelHistory,
  deleteChannelEdits,
  updateChannelEditStatus,
} from "../api/channels/channelServices";
import { useAuth, UserRole } from "./AuthContext";
import { ContentAgeRestriction } from "../api/enums";

export interface CreateChannelContextType {
  channelData: SKChannel;
  updateChannelData: React.Dispatch<React.SetStateAction<SKChannel>>;
  loading: boolean;
  setChannelId: (channelId: string) => void;
  updateDataOnServer?: () => void;
  updateStatus?: (contentId: string, actionItem: ActionItem) => void;
  // isEdit?: boolean;
  isModal?: boolean;
  originalState?: { status: ChannelState; subStatus: ChannelSubstate };
}

const signupInitialState: CreateChannelContextType = {
  setChannelId: () => {},
  channelData: {
    name: "",
    description: "",
    profileImageUrl: "",
    backgroundImageUrl: "",
    language: "",
    secondLanguage: "",
    country: "",
    externalLinks: null,
    topic: null,
    certifyFirst: false,
    certifySecond: false,
    autoPublish: false,
    status: ChannelState.DRAFT,
    subStatus: ChannelSubstate.NONE,
    subscriptionStatus: Tier.free,
  },
  updateChannelData: () => {},
  loading: false,
  // isEdit: false,
  isModal: true,
};

export const CreateChannelContext = createContext<CreateChannelContextType>(signupInitialState);

interface CreateChannelProviderProps {
  children: ReactNode;
  // isEdit?: boolean;
  isModal?: boolean;
}

const CreateChannelProvider = ({ children, isModal = true }: CreateChannelProviderProps) => {
  const authContext = useAuth();

  const [channelData, updateChannelData] = useState<SKChannel>({
    name: "",
    description: "",
    profileImageUrl: "",
    backgroundImageUrl: "",
    language: "None",
    secondLanguage: "None",
    country: "None",
    externalLinks: null,
    topic: null,
    certifyFirst: false,
    certifySecond: false,
    autoPublish: false,
    status: ChannelState.DRAFT,
    subStatus: ChannelSubstate.NONE,
    channelType: authContext?.userRoles?.includes(UserRole.BrandAdmin) ? ChannelType.brand : ChannelType.creator,
    subscriptionStatus: Tier.free,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [originalState, setOriginalState] = useState<{ status: ChannelState; subStatus: ChannelSubstate } | undefined>(undefined);
  const [channelId, setChannelId] = useState<string | null>(null);

  const updateDataOnServer = useCallback(async () => {
    setLoading(true);
    if (channelData) {
      if (channelData.id) {
        // if (isEdit) {
        //   await updateChannelEdits(channelData.id!, channelData);
        // } else {
        await updateChannel(channelData.id!, channelData);
        // }
      } else {
        // if (isEdit) {
        //   if (authContext && authContext.myChannel && authContext.myChannel.id) {
        //     await createChannelEdits(authContext.myChannel.id, channelData);
        //     updateChannelData({ ...channelData, id: authContext.myChannel.id });
        //   }
        // } else {
        const id = await createChannel(channelData);
        updateChannelData({ ...channelData, id });
        // }
      }
    }
    setLoading(false);
  }, [channelData]);

  const updateStatus = async (contentId: string, actionItem: ActionItem) => {
    var data: any = {};
    setLoading(true);
    switch (actionItem) {
      case ActionItem.submit_for_review:
        // await updateChannelStatus(contentId, ChannelState.IN_REVIEW, ChannelSubstate.NONE);
        data = { status: ChannelState.IN_REVIEW, subStatus: ChannelSubstate.NONE };
        await updateChannel(contentId, { ...channelData, ...data });
        await updateChannelHistory(contentId, "Submitted for Review");
        authContext?.getChannel();
        break;
      case ActionItem.edit_submit:
        data = { status: ChannelState.ACTIVE, subStatus: ChannelSubstate.EDIT_IN_REVIEW };
        await updateChannelEdits(contentId, { ...channelData, ...data });
        await updateChannelEditStatus(contentId, ChannelState.ACTIVE, ChannelSubstate.EDIT_IN_REVIEW);
        setOriginalState({ status: ChannelState.ACTIVE, subStatus: ChannelSubstate.EDIT_IN_REVIEW });
        break;
      case ActionItem.withdraw:
        await updateChannelStatus(contentId, ChannelState.DRAFT, ChannelSubstate.WITHDRAWN);
        data = { status: ChannelState.DRAFT, subStatus: ChannelSubstate.WITHDRAWN };
        (channelData.subStatus === ChannelSubstate.EDIT_IN_DRAFT ||
          channelData.subStatus === ChannelSubstate.EDIT_REJECTED ||
          channelData.subStatus === ChannelSubstate.EDIT_IN_REVIEW) &&
          (await deleteChannelEdits(contentId));
        break;
      case ActionItem.delete:
        deleteChannel(contentId);
        break;
      default:
    }
    updateChannelData(prev => ({ ...prev, ...data }));
    setLoading(false);
  };

  // const updateDataOnServer = async () => {
  //   setLoading(true);
  //   if (channelData) {
  //     if (channelData.id) {
  //       await updateChannel(channelData.id!, channelData);
  //     } else {
  //       const id = await createChannel(channelData);
  //       updateChannelData({ ...channelData, id });
  //     }
  //   }
  //   setLoading(false);
  // };

  // useEffect(() => {
  //   const getChannelLocal = async (channelId: string) => {
  //     let channel = await getChannel(channelId);
  //     updateChannelData(channel);
  //   };

  //   if (channelId) {
  //     getChannelLocal(channelId);
  //   }
  //   return () => {};
  // }, [channelId]);

  useEffect(() => {
    const getChannel = async () => {
      setLoading(true);
      const channelnew = await getMyChannel();
      if (channelnew) {
        updateChannelData(prev => ({ ...prev, ...channelnew }));
      } else {
        const id = await createChannel(channelData);
        if (id) {
          updateChannelData(prev => ({ ...prev, id }));
        }
      }
      setLoading(false);
    };

    const getEditChannel = async () => {
      setLoading(true);
      if (authContext && authContext.myChannel && authContext.myChannel.id) {
        try {
          const { status, subStatus } = await getMyChannel();
          const channelnew = await getChannelEdits(authContext.myChannel.id);
          setOriginalState({ status, subStatus });
          updateChannelData(prev => ({ ...prev, ...channelnew }));
        } catch (error) {
          const channelnew = await getMyChannel();
          const { id, ...rest } = channelnew;
          const channelEdits = await createChannelEdits(authContext.myChannel.id, rest);
          setOriginalState({ status: channelEdits.status, subStatus: channelEdits.subStatus });
          updateChannelData({ ...channelEdits, id });
        }
      }
      setLoading(false);
    };
    // isEdit ? getEditChannel() : getChannel();
    getChannel();
  }, [authContext]);

  // useEffect(() => {
  //   const getChannel = async () => {
  //     setLoading(true);
  //     const channelnew = await getMyChannel();
  //     updateChannelData(prev => ({ ...prev, ...channelnew }));
  //     setLoading(false);
  //   };
  //   if (!isEdit) {
  //     getChannel();
  //   }
  // }, [i]);

  return (
    <CreateChannelContext.Provider
      value={{ setChannelId, channelData, updateDataOnServer, updateStatus, isModal, originalState, updateChannelData, loading }}
    >
      {children}
    </CreateChannelContext.Provider>
  );
};

export default CreateChannelProvider;
